import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Badge } from 'features/catalog/useBadges';
import { Profile } from 'features/profiles/useProfiles';
import { JOURNEYS } from 'features/catalog/badge-journeys.ts';
import { getFirstPartOfEmail } from 'shared/lib/email';
import {
  computeRarityCounts,
  getRarityImageUrlByName,
  getRarityScoreFromName,
  RARITY_TIERS,
  RarityCounts,
} from 'features/catalog/rarity';

export interface CompletedJourney {
  id: string;
  emoji: string;
  name: string;
  points: number;
  rarity: string;
}

export interface ScoreBreakdown {
  name: string;
  sortScore: number;
  badgeCounts: RarityCounts;
  completedJourneys: CompletedJourney[];
}

/**
 * Computes the score breakdown for a profile identified by email.
 * Returns null if the profile is not found.
 */
export const getScoreBreakdown = (
  email: string,
  profilesMap: Map<string, Profile>,
  badgesMap: Map<string, Badge>
): ScoreBreakdown | null => {
  const profile = profilesMap.get(getFirstPartOfEmail(email));
  if (!profile) return null;

  const badgeCounts = computeRarityCounts(profile.badgesIDs, badgesMap);

  // Get completed journeys in the order they appear in profile.journeyEmojis (claim score, coolest first)
  const completedJourneys: CompletedJourney[] = profile.journeyEmojis
    .map((emoji) => JOURNEYS.find((journey) => journey.emoji === emoji))
    .filter((journey): journey is (typeof JOURNEYS)[0] => journey !== undefined)
    .map((journey) => ({
      id: journey.id,
      emoji: journey.emoji,
      name: journey.name,
      points: getRarityScoreFromName(journey.rarity),
      rarity: journey.rarity,
    }));

  return {
    name: profile.name,
    sortScore: profile.sortScore,
    badgeCounts,
    completedJourneys,
  };
};

interface ScoreBreakdownTooltipProps {
  /** Current tooltip state (target value + bounding rect), or null if hidden. */
  scoreTooltip: { value: string; rect: DOMRect } | null;
  /** Profiles map for looking up the user. */
  profilesMap: Map<string, Profile>;
  /** Badges map for rarity calculations. */
  badgesMap: Map<string, Badge>;
}

/**
 * Floating tooltip rendered via portal that shows a detailed score breakdown
 * (badge counts by rarity + journey bonus) next to the hovered dropdown option.
 */
const EDGE_PADDING = 8;

export const ScoreBreakdownTooltip: React.FC<ScoreBreakdownTooltipProps> = ({
  scoreTooltip,
  profilesMap,
  badgesMap,
}) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [top, setTop] = useState<number | null>(null);

  useLayoutEffect(() => {
    if (!scoreTooltip || !tooltipRef.current) {
      setTop(null);
      return;
    }
    const height = tooltipRef.current.getBoundingClientRect().height;
    const centerTarget = scoreTooltip.rect.top + scoreTooltip.rect.height / 2;
    const desiredTop = centerTarget - height / 2;
    const maxTop = Math.max(EDGE_PADDING, window.innerHeight - height - EDGE_PADDING);
    setTop(Math.min(Math.max(desiredTop, EDGE_PADDING), maxTop));
  }, [scoreTooltip]);

  if (!scoreTooltip) return null;

  const breakdown = getScoreBreakdown(scoreTooltip.value, profilesMap, badgesMap);
  if (!breakdown) return null;

  const { rect } = scoreTooltip;
  const gap = 8;
  const tooltipWidth = 300;
  const fitsRight = rect.right + gap + tooltipWidth <= window.innerWidth;
  const left = fitsRight ? rect.right + gap : rect.left - gap - tooltipWidth;

  return createPortal(
    <div
      ref={tooltipRef}
      className="score-breakdown-tooltip"
      style={{
        position: 'fixed',
        left,
        top: top ?? rect.top + rect.height / 2,
        transform: top === null ? 'translateY(-50%)' : undefined,
        visibility: top === null ? 'hidden' : 'visible',
        zIndex: 10000,
      }}
    >
      <div className="score-breakdown-tooltip-header">
        {breakdown.name}
        <span className="score-breakdown-tooltip-score">{breakdown.sortScore} pts</span>
      </div>

      {/* Badges section */}
      {RARITY_TIERS.some(({ key }) => breakdown.badgeCounts[key] > 0) && (
        <>
          <div className="score-breakdown-tooltip-section-header">Badges</div>
          {RARITY_TIERS.map(({ key, score, label }) => {
            const badges = breakdown.badgeCounts[key];
            if (badges === 0) return null;
            return (
              <div key={key} className="score-breakdown-tooltip-row" style={{ justifyContent: 'space-between' }}>
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                  }}
                >
                  <img src={getRarityImageUrlByName(key)} alt={label} className="score-breakdown-tooltip-icon" />
                  <span>
                    {badges} {label} × {score}
                  </span>
                </span>
                <span className="score-breakdown-tooltip-score">+{badges * score} pts</span>
              </div>
            );
          })}
        </>
      )}

      {/* Journeys section */}
      {breakdown.completedJourneys.length > 0 && (
        <>
          <div className="score-breakdown-tooltip-section-divider"></div>
          <div className="score-breakdown-tooltip-section-header">Journeys</div>
          {breakdown.completedJourneys.slice(0, 3).map((journey) => {
            return (
              <div
                key={`${journey.emoji}-${journey.name}`}
                className="score-breakdown-tooltip-row"
                style={{ justifyContent: 'space-between' }}
              >
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                  }}
                >
                  <img
                    src={getRarityImageUrlByName(journey.rarity)}
                    alt={journey.rarity}
                    className="score-breakdown-tooltip-icon"
                  />
                  <span>{journey.emoji}</span>
                  <span>{journey.name}</span>
                </span>
                <span className="score-breakdown-tooltip-score">+{journey.points} pts</span>
              </div>
            );
          })}
          {breakdown.completedJourneys.length > 3 &&
            (() => {
              const otherJourneys = breakdown.completedJourneys.slice(3);
              const otherJourneysTotal = otherJourneys.reduce((sum, journey) => sum + journey.points, 0);
              // Find the rarest journey (highest points = rarest)
              const rarestJourney = otherJourneys.reduce((rarest, journey) =>
                journey.points > rarest.points ? journey : rarest
              );

              return (
                <div className="score-breakdown-tooltip-row" style={{ justifyContent: 'space-between' }}>
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.4rem',
                    }}
                  >
                    <img
                      src={getRarityImageUrlByName(rarestJourney.rarity)}
                      alt={rarestJourney.rarity}
                      className="score-breakdown-tooltip-icon"
                    />
                    <span>{otherJourneys.length} more Journeys</span>
                  </span>
                  <span className="score-breakdown-tooltip-score">+{otherJourneysTotal} pts</span>
                </div>
              );
            })()}
        </>
      )}
    </div>,
    document.body
  );
};
